import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';

import {
  addFAQService,
  deleteFAQService,
  getFAQByIdService,
  getFAQListService,
  updateFAQOrderService,
  updateFAQService,
} from '../../services/faqs.service';

const initialState = {
  faqsList: {
    totalCount: 0,
    totalPage: 0,
    faqData: [],
  },
  allFaqsList: [],
  faqDetails: null,
};

export const updateFAQOrder = createAsyncThunk(
  `UPDATE_FAQ_ORDER`,
  async (body, { rejectWithValue }) => {
    try {
      const response = await updateFAQOrderService(body);
      return response;
    } catch (error) {
      throw rejectWithValue(error);
    }
  }
);

export const getFAQsList = createAsyncThunk(
  `GET_FAQs`,
  async ({ page, limit, sortField, sortType, search }, { rejectWithValue }) => {
    try {
      const response = await getFAQListService(
        true,
        page,
        limit,
        sortField,
        sortType,
        search
      );
      return response;
    } catch (error) {
      throw rejectWithValue(error);
    }
  }
);

export const getAllFAQsList = createAsyncThunk(
  `GET_ALL_FAQs`,
  async (_, { rejectWithValue }) => {
    try {
      const response = await getFAQListService(
        false,
        0,
        0,
        'priorityIndex',
        'ASC'
      );
      return response;
    } catch (error) {
      throw rejectWithValue(error);
    }
  }
);

export const addFAQ = createAsyncThunk(
  'ADD_FAQ',
  async (body, { rejectWithValue }) => {
    try {
      const response = await addFAQService(body);
      return response;
    } catch (error) {
      throw rejectWithValue(error);
    }
  }
);

export const getFAQById = createAsyncThunk(
  'GET_FAQ_BY_ID',
  async (id, { rejectWithValue }) => {
    try {
      const response = await getFAQByIdService(id);
      return response;
    } catch (error) {
      throw rejectWithValue(error);
    }
  }
);

export const updateFAQ = createAsyncThunk(
  'UPDATE_FAQ',
  async (body, { rejectWithValue }) => {
    try {
      const response = await updateFAQService(body);
      return response;
    } catch (error) {
      throw rejectWithValue(error);
    }
  }
);

export const deleteFAQ = createAsyncThunk(
  'DELETE_FAQ',
  async (id, { rejectWithValue }) => {
    try {
      const response = await deleteFAQService(id);
      return response;
    } catch (error) {
      throw rejectWithValue(error);
    }
  }
);

const faqsSlice = createSlice({
  name: 'faqsSlice',
  initialState,
  reducers: {
    resetFAQDetails: (state) => {
      state.faqDetails = null;
    },
    resetFAQsList: (state) => {
      state.faqsList = {
        totalCount: 0,
        totalPage: 0,
        faqData: [],
      };
    },
    resetAllFAQs: (state) => {
      state.allFaqsList = [];
    },
  },
  extraReducers: (builder) => {
    // update FAQ Order
    builder
      .addCase(updateFAQOrder.fulfilled, (state, { payload }) => {
        if (!payload?.data?.isError) {
          toast.success(payload?.data?.message);
        }
      })
      .addCase(updateFAQOrder.rejected, (state, { payload }) => {
        toast.error(
          payload?.response?.data?.message ?? 'Something went wrong!'
        );
      });

    // Get All FAQs List
    builder
      .addCase(getAllFAQsList.fulfilled, (state, { payload }) => {
        if (!payload?.data?.isError) {
          state.allFaqsList = [...(payload?.data?.data?.faqList ?? [])];
        }
      })
      .addCase(getAllFAQsList.rejected, (state, { payload }) => {
        toast.error(
          payload?.response?.data?.message ?? 'Something went wrong!'
        );
      });

    // Get FAQs List
    builder
      .addCase(getFAQsList.fulfilled, (state, { payload }) => {
        if (!payload?.data?.isError) {
          const { count, totalPage, faqList } = payload?.data?.data;
          state.faqsList = {
            totalCount: count ?? 0,
            totalPage: totalPage ?? 0,
            faqData: [...faqList],
          };
        }
      })
      .addCase(getFAQsList.rejected, (state, { payload }) => {
        toast.error(
          payload?.response?.data?.message ?? 'Something went wrong!'
        );
      });

    // Add FAQ
    builder
      .addCase(addFAQ.fulfilled, (state, { payload }) => {
        if (!payload?.data?.isError) {
          toast.success(`${payload?.data?.message}`);
        }
      })
      .addCase(addFAQ.rejected, (state, { payload }) => {
        toast.error(
          payload?.response?.data?.message ?? 'Something went wrong!'
        );
      });

    // Get FAQ Details
    builder
      .addCase(getFAQById.fulfilled, (state, { payload }) => {
        if (!payload?.data?.isError) {
          state.faqDetails = payload?.data?.data ?? null;
        }
      })
      .addCase(getFAQById.rejected, (_, { payload }) => {
        toast.error(
          payload?.response?.data?.message ?? 'Something went wrong!'
        );
      });

    // Update FAQ
    builder
      .addCase(updateFAQ.fulfilled, (_, { payload }) => {
        if (!payload?.data?.isError) {
          toast.success(`${payload?.data?.message}`);
        }
      })
      .addCase(updateFAQ.rejected, (_, { payload }) => {
        toast.error(
          payload?.response?.data?.message ?? 'Something went wrong!'
        );
      });

    // Delete FAQ
    builder
      .addCase(deleteFAQ.fulfilled, (_, { payload }) => {
        if (!payload?.data?.isError) {
          toast.success(`${payload?.data?.message}`);
        }
      })
      .addCase(deleteFAQ.rejected, (_, { payload }) => {
        toast.error(
          payload?.response?.data?.message ?? 'Something went wrong!'
        );
      });
  },
});

export const { resetFAQDetails, resetAllFAQs, resetFAQsList } =
  faqsSlice.actions;

export default faqsSlice.reducer;
